import { useState, useEffect, useCallback } from 'react';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';

export const useWidgetConfig = <T extends Record<string, unknown>>(
  widgetId: string,
  defaultConfig: T
) => {
  const [config, setConfig] = useState<T>(defaultConfig);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('widgets')
          .select('metadata')
          .eq('id', widgetId)
          .eq('user_id', userId)
          .single();

        if (error) throw error;

        // If metadata exists and has content, merge it with default config
        if (data?.metadata) {
          setConfig(prevConfig => ({
            ...prevConfig,
            ...data.metadata,
          }));
        }
      });
    } catch (error) {
      handleException(error);
    } finally {
      setLoading(false);
    }
  }, [widgetId]);

  const updateConfig = useCallback(async (newConfig: Partial<T>) => {
    // Optimistically update the local state
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig,
    }));

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('widgets')
          .update({
            metadata: {
              ...config,
              ...newConfig,
            },
          })
          .eq('id', widgetId)
          .eq('user_id', userId);

        if (error) throw error;
      });
    } catch (error) {
      // Revert to previous state if update fails
      setConfig(config);
      handleException(error);
    }
  }, [widgetId, config]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    updateConfig,
    loading,
  };
};
